/**
 * Profils de quotas — source de vérité unique.
 *
 * Volontairement séparé de `components/pages/pricingData.tsx` : ce fichier est lu
 * par l'affichage ET par l'application des quotas, alors que `pricingData` ne
 * décrit que la grille publique. `pilote` est provisionné à la main après un
 * rendez-vous et n'apparaît sur aucune page.
 */

export type PlanId = 'beta' | 'pilote';

export interface PlanQuotas {
  /** Plafond du vivier : candidats analysés et stockés. */
  candidates: number;
  /**
   * Missions analysables par mois. `null` = illimité.
   * Une mission coûte ~0,005 $ à analyser : la brider n'apporte rien et rend
   * incohérent le quota de matchings (l'usage réel est de 3 à 5 matchings par mission).
   */
  missionsPerMonth: number | null;
  /** Matchings lançables par mois. SEUL quota qui borne réellement la dépense (~92 % du coût). */
  matchingsPerMonth: number;
  /**
   * Profils envoyés au LLM à chaque matching (`top_k` côté moteur).
   * Ce n'est PAS le nombre de résultats affichés : tant que P2.8 (cf.
   * `matching-remediation.md`) n'est pas fait, le front affiche tout ce qui a été
   * analysé, donc les deux nombres sont confondus.
   */
  analyzedPerMatching: number;
}

export interface Plan {
  id: PlanId;
  label: string;
  quotas: PlanQuotas;
  /** Présent sur la grille publique, ou provisionné à la main. */
  publiclyListed: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  beta: {
    id: 'beta',
    label: 'Bêta',
    publiclyListed: true,
    quotas: {
      candidates: 200,
      missionsPerMonth: 20,
      matchingsPerMonth: 20,
      analyzedPerMatching: 10,
    },
  },
  pilote: {
    id: 'pilote',
    label: 'Pilote',
    publiclyListed: true,
    quotas: {
      candidates: 500,
      missionsPerMonth: null,
      matchingsPerMonth: 100,
      analyzedPerMatching: 20,
    },
  },
};

export const DEFAULT_PLAN: PlanId = 'beta';

export const getQuotas = (id: PlanId): PlanQuotas => PLANS[id].quotas;

/**
 * Libellés dérivés des quotas : la carte tarifaire et le moteur ne peuvent pas
 * diverger, puisqu'ils lisent le même objet.
 */
export function quotaBullets(id: PlanId): string[] {
  const q = getQuotas(id);
  return [
    `${q.candidates} candidats analysés & stockés`,
    q.missionsPerMonth === null ? 'Missions illimitées' : `${q.missionsPerMonth} missions /mois`,
    `${q.matchingsPerMonth} matchings /mois`,
    `${q.analyzedPerMatching} profils analysés par matching`,
  ];
}
